import { useState, useEffect } from 'react'
import {
  getWaitlistEntries,
  getWaitlistStats,
  updateWaitlistEntry,
  deleteWaitlistEntry
} from '../../lib/supabase'
import { format } from 'date-fns'
import {
  Users,
  Clock,
  CheckCircle,
  Phone,
  Mail,
  Search,
  Download,
  Trash2,
  AlertCircle,
  TrendingUp,
  Sparkles
} from 'lucide-react'

const StatCard = ({ title, value, icon: Icon, color = 'primary' }) => {
  const colors = {
    primary: 'bg-rdc-primary/10 text-rdc-primary',
    gold: 'bg-rdc-gold/20 text-rdc-gold',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600'
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-rdc-taupe text-sm">{title}</span>
        <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-2xl font-bold text-black">{value}</p>
    </div>
  )
}

export default function Waitlist() {
  const [entries, setEntries] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [entriesRes, statsRes] = await Promise.all([
        getWaitlistEntries(),
        getWaitlistStats()
      ])

      if (entriesRes.error) throw entriesRes.error
      if (statsRes.error) throw statsRes.error

      setEntries(entriesRes.data || [])
      setStats(statsRes.data)
    } catch (err) {
      console.error('Error loading waitlist:', err)
      setError('Failed to load waitlist data')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusChange = async (id, newStatus) => {
    try {
      const { error } = await updateWaitlistEntry(id, { status: newStatus })
      if (error) throw error

      // Update local state
      setEntries(prev =>
        prev.map(e => (e.id === id ? { ...e, status: newStatus } : e))
      )

      // Refresh stats
      const { data: newStats } = await getWaitlistStats()
      if (newStats) setStats(newStats)
    } catch (err) {
      console.error('Error updating status:', err)
      setError('Failed to update status')
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to remove this entry?')) return

    try {
      const { error } = await deleteWaitlistEntry(id)
      if (error) throw error

      setEntries(prev => prev.filter(e => e.id !== id))

      // Refresh stats
      const { data: newStats } = await getWaitlistStats()
      if (newStats) setStats(newStats)
    } catch (err) {
      console.error('Error deleting entry:', err)
      setError('Failed to delete entry')
    }
  }

  const exportToCSV = () => {
    const headers = ['Email', 'Phone', 'Name', 'Interest', 'Status', 'Source', 'Date']
    const rows = filteredEntries.map(e => [
      e.email,
      e.phone || '',
      e.full_name || '',
      e.interest || '',
      e.status,
      e.source || '',
      format(new Date(e.created_at), 'yyyy-MM-dd')
    ])

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `concierge-waitlist-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Filter entries
  const filteredEntries = entries.filter(entry => {
    const matchesSearch =
      entry.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.phone?.includes(searchTerm)

    const matchesStatus = filterStatus === 'all' || entry.status === filterStatus

    return matchesSearch && matchesStatus
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-rdc-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl font-display font-bold text-black">Concierge Waitlist</h1>
            <Sparkles className="w-5 h-5 text-rdc-gold" />
          </div>
          <p className="text-rdc-taupe">Manage waitlist signups for the concierge service</p>
        </div>

        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-rdc-warm-gray rounded-lg text-rdc-dark-gray hover:bg-rdc-cream transition-colors"
        >
          <Download size={18} />
          Export CSV
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-sm underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard title="Total Signups" value={stats.total} icon={Users} color="primary" />
          <StatCard title="Pending" value={stats.pending} icon={Clock} color="gold" />
          <StatCard title="Contacted" value={stats.contacted} icon={Phone} color="blue" />
          <StatCard title="Enrolled" value={stats.enrolled} icon={CheckCircle} color="green" />
        </div>
      )}

      {/* Interest Breakdown */}
      {stats?.byInterest && Object.keys(stats.byInterest).length > 0 && (
        <div className="bg-white rounded-xl p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-rdc-primary" />
            <h2 className="font-semibold text-black">Interest Breakdown</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {Object.entries(stats.byInterest).map(([interest, count]) => (
              <div
                key={interest}
                className="px-3 py-2 bg-rdc-cream rounded-lg"
              >
                <span className="text-sm text-rdc-taupe capitalize">
                  {interest.replace('-', ' ')}:
                </span>
                <span className="ml-2 font-semibold text-black">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-rdc-taupe" />
            <input
              type="text"
              placeholder="Search by email, name, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-rdc-warm-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-rdc-warm-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="contacted">Contacted</option>
            <option value="enrolled">Enrolled</option>
            <option value="declined">Declined</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl overflow-hidden">
        {filteredEntries.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-rdc-warm-gray mx-auto mb-4" />
            <h3 className="font-semibold text-black mb-2">No entries found</h3>
            <p className="text-rdc-taupe">
              {entries.length === 0
                ? 'Waitlist signups will appear here'
                : 'Try adjusting your search or filters'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-rdc-cream">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-rdc-taupe uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-rdc-taupe uppercase tracking-wider">
                    Interest
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-rdc-taupe uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-rdc-taupe uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-rdc-taupe uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-rdc-warm-gray/40">
                {filteredEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-rdc-cream/50">
                    <td className="px-6 py-4">
                      <div>
                        {entry.full_name && (
                          <p className="font-medium text-black">{entry.full_name}</p>
                        )}
                        <div className="flex items-center gap-1 text-sm text-rdc-taupe">
                          <Mail className="w-3 h-3" />
                          <span>{entry.email}</span>
                        </div>
                        {entry.phone && (
                          <div className="flex items-center gap-1 text-sm text-rdc-taupe">
                            <Phone className="w-3 h-3" />
                            <span>{entry.phone}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="capitalize text-sm text-rdc-dark-gray">
                        {entry.interest?.replace('-', ' ') || 'General'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={entry.status}
                        onChange={(e) => handleStatusChange(entry.id, e.target.value)}
                        className={`px-2 py-1 text-xs font-medium rounded-full border-0 cursor-pointer ${
                          entry.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700'
                            : entry.status === 'contacted'
                            ? 'bg-blue-100 text-blue-700'
                            : entry.status === 'enrolled'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        <option value="pending">Pending</option>
                        <option value="contacted">Contacted</option>
                        <option value="enrolled">Enrolled</option>
                        <option value="declined">Declined</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-sm text-rdc-taupe">
                      {format(new Date(entry.created_at), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleDelete(entry.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete entry"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Count */}
      {filteredEntries.length > 0 && (
        <p className="text-sm text-rdc-taupe mt-4">
          Showing {filteredEntries.length} of {entries.length} entries
        </p>
      )}
    </div>
  )
}
